import { PropsWithChildren } from 'react';

import {
  useHostingAdvancedConsole,
  useHostingAdvancedSystemConsole,
} from '@newfold/huapi-js';

import useAccount from '~/hooks/useAccount';
import useIsConsoleAccessDisabled from '~/Nova/scenes/HostingList/scenes/SelfManagedHosting/hooks/useIsConsoleAccessDisabled';

import SSOComponent from '../index';
import { SSO_HOOK_OPTIONS } from '../utils/helpers';
import { BaseSsoProps } from '../utils/types';

interface SystemConsoleSSOProps extends BaseSsoProps {
  hostingId?: string | number;
}

const SYSTEM_CONSOLE_APPS = new Set(['systemconsole', 'systemConsoleSso']);

const SystemConsoleSSO = ({
  children = undefined,
  hostingId = undefined,
  queryParams = {},
  disabled = false,
  ...props
}: PropsWithChildren<SystemConsoleSSOProps>) => {
  const { id: accountHostingId } = useAccount();
  const accountId = hostingId || accountHostingId;
  const app = queryParams?.app;
  const isSsh = app === 'ssh';

  const isSystemConsole = SYSTEM_CONSOLE_APPS.has(app ?? '');

  const isConsoleAccessDisabled = useIsConsoleAccessDisabled(
    accountId,
    isSsh ? 'terminal' : 'launch',
  );

  const systemConsoleSso = useHostingAdvancedSystemConsole(accountId, {
    query: {
      ...SSO_HOOK_OPTIONS.query,
      enabled: isSystemConsole,
    },
  });

  const consoleSso = useHostingAdvancedConsole(
    String(accountId),
    isSsh ? { protocol: 'ssh' as const } : undefined,
    {
      query: {
        ...SSO_HOOK_OPTIONS.query,
        enabled: isSsh,
      },
    },
  );

  const activeSso = isSsh ? consoleSso : systemConsoleSso;
  const isQuickLinksDisabled =
    (isSsh || isSystemConsole) && isConsoleAccessDisabled;

  const isDisabled = disabled || isQuickLinksDisabled;
  return (
    <SSOComponent
      sso={activeSso}
      isDisabled={isDisabled}
      hostingId={hostingId}
      {...props}
    >
      {children}
    </SSOComponent>
  );
};

export default SystemConsoleSSO;
